// @flow

import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { GSGrid, Icons } from "@components";
import type { DocumentType } from "@modules/shared/reducer/employee-events/types";
import actionCreators from "@modules/shared/actionCreator";
import * as selectors from "@modules/shared/selectors/employee-events";
import type { State } from "@store/reducers";
import { compose } from "redux";
import { toTitleCase } from "@transformers/response";
import { formatDateTime } from "@transformers/date";
import { formatAuthoredBy } from "@transformers/names";
import type { EmployeeType } from "@modules/employee/types";
import { getEmployee, getEmployeeName } from "@modules/employee/selectors";
import { RemoveDocumentModal } from "@modules/shared/components";

type OwnProps = $Exact<{
  data: Array<DocumentType>,
  employeeId: number,
  clientId: number,
}>;

type PropsFromState = $Exact<{
  employee: EmployeeType,
  showRemoveDocumentModal: boolean,
  documentId: number | null,
  documentName: string,
}>;

type PropsFromDispatch = $Exact<{
  onRemoveDocumentHandler: typeof actionCreators.ToggleRemoveEmployeeDocumentModal,
  onDismissRemoveDocumentHandler: Function,
  removeDocument: typeof actionCreators.RemoveEmployeeDocument,
  downloadDocument: typeof actionCreators.DownloadEmployeeDocument,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class DocumentsList extends React.Component<Props> {
  removeDocumentHandler = (item: DocumentType) => () => {
    this.props.onRemoveDocumentHandler({
      visible: true,
      documentId: item.id,
      documentName: item.name,
    });
  };

  removeDocument = () => {
    this.props.removeDocument({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      documentId: this.props.documentId,
    });
  };

  downloadDocument = (item: DocumentType) => () => {
    const { clientId, employeeId } = this.props;

    this.props.downloadDocument({
      clientId,
      employeeId,
      fileId: item.id,
      fileName: item.name,
    });
  };

  get columns() {
    return [
      {
        key: "id",
        name: this.props.t("ID"),
      },
      {
        key: "fileType",
        name: this.props.t("TYPE"),
        renderCell: ({ row }: { row: DocumentType }) => toTitleCase(this.props.t(row.fileType)),
      },
      {
        key: "name",
        name: this.props.t("FILE_NAME"),
      },
      {
        key: "createdAt",
        name: this.props.t("DATE_UPLOADED"),
        renderCell: ({ row }: { row: DocumentType }) => formatDateTime(row.createdAt),
      },
      {
        key: "createdBy",
        name: this.props.t("UPLOADED_BY"),
        renderCell: ({ row }: { row: DocumentType }) => formatAuthoredBy(row.createdBy),
      },
      {
        key: "actions",
        name: "",
        renderCell: ({ row }: { row: DocumentType }) => (
          <div className="d-flex justify-content-end">
            {!row.isArchived && (
              <span className="material-icons pointer" onClick={this.downloadDocument(row)}>
                file_download
              </span>
            )}
            <span
              className="material-icons pointer font-size-26 ml-3"
              onClick={this.removeDocumentHandler(row)}
            >
              delete
            </span>
          </div>
        ),
      },
    ];
  }

  render() {
    return (
      <React.Fragment>
        <GSGrid rows={this.props.data} columns={this.columns} />
        {this.props.showRemoveDocumentModal && (
          <RemoveDocumentModal
            onDismissHandler={this.props.onDismissRemoveDocumentHandler}
            onSubmit={this.removeDocument}
            documentName={this.props.documentName}
            employeeName={getEmployeeName(this.props.employee)}
          />
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: State, ownProps: OwnProps): PropsFromState => ({
  employee: getEmployee(ownProps.employeeId)(state),
  showRemoveDocumentModal: selectors.showRemoveDocumentModal(state),
  documentId: selectors.getDocumentId(state),
  documentName: selectors.getDocumentName(state) || "",
});

const mapDispatchToProps: PropsFromDispatch = {
  onRemoveDocumentHandler: actionCreators.ToggleRemoveEmployeeDocumentModal,
  onDismissRemoveDocumentHandler: () =>
    actionCreators.ToggleRemoveEmployeeDocumentModal({ visible: false }),
  removeDocument: actionCreators.RemoveEmployeeDocument,
  downloadDocument: actionCreators.DownloadEmployeeDocument,
};

export default compose(connect(mapStateToProps, mapDispatchToProps), localize())(DocumentsList);
